import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { functions, db } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ClipboardList, Plus, Search, Filter, Download, Upload, Eye, Edit, Trash2, Package, Building2, Tag, Calendar, User, AlertCircle, CheckCircle, Clock, X, Info, Loader2, Truck } from 'lucide-react';
import {
  buildCatalogoMapFromRows,
  avaliarAderenciaCategorias,
  rotuloMotivosAderencia,
} from '../utils/categoriasInsumoAderencia';
import ProductModal from './ProductModal';
import ProdutoFornecedoresModal from './ProdutoFornecedoresModal';
import {
  mensagemBloqueioExclusaoPorEstoque,
  parseErroExclusaoInsumo,
  verificarEstoqueAntesExcluirInsumo,
} from '../utils/excluirInsumoEstoque';
import { filterItemsBySearch, sanitizeSearchQuery } from '../utils/textSanitize';

/** Na página Insumos (quatro menus): reorganizar itens alterando `tipo_insumo` em lote (Cloud Function bulkSetTipoInsumoProducts). */
export const EXIBIR_HELPER_REORGANIZAR_TIPO_INSUMO = true;

const OPCOES_TIPO_INSUMO_DESTINO = [
  { value: 'ingrediente', label: 'Ingrediente' },
  { value: 'embalagem', label: 'Embalagem' },
  { value: 'servico', label: 'Serviço' },
  { value: 'limpeza_conservacao', label: 'Limpeza e conservação' },
];

/** Normaliza tipo legado `materia_prima` → `ingrediente` para filtro e exibição. */
function tipoInsumoNormalizado(doc) {
  const t = doc?.tipo_insumo;
  if (!t || t === 'materia_prima') return 'ingrediente';
  return t;
}

const LABEL_FREQUENCIA_EXECUCAO = {
  diaria: 'Diária',
  semanal: 'Semanal',
  quinzenal: 'Quinzenal',
  mensal: 'Mensal',
  bimestral: 'Bimestral',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
  sob_demanda: 'Sob demanda',
};

function labelFrequenciaExecucao(val) {
  if (!val) return '—';
  return LABEL_FREQUENCIA_EXECUCAO[val] || val;
}

const ProductsPage = ({ insumoTipoFilter }) => {
  const { user, userRole, companyId, userProfile, getEmpresaId, isRole, hasPermission } = useAuth();
  const isFornecedor = isRole('fornecedor');
  const isRecebedor = isRole('recebedor');

  // ✅ NOVO: Funções para produtos globais (apenas superadmin)
  const updateProdutoRegulaMais = httpsCallable(functions, 'updateProdutoRegulaMais');
  const deleteProdutoRegulaMais = httpsCallable(functions, 'deleteProdutoRegulaMais');
  const createProdutoRegulaMais = httpsCallable(functions, 'createProdutoRegulaMais');
  const importarProdutosDaBase = httpsCallable(functions, 'importarProdutosDaBase');
  const createProductFn = httpsCallable(functions, 'createProduct');
  const updateProductFn = httpsCallable(functions, 'updateProduct');

  // Estados principais
  // ✅ NOVO: Se for superadmin, inicializar com viewMode 'global'
  // ✅ CORREÇÃO: Recebedor sempre deve ver apenas a empresa (sem acesso à base global)
  const isSuperadmin = isRole('superadmin');

  const isInsumoRota =
    insumoTipoFilter === 'ingrediente' ||
    insumoTipoFilter === 'embalagem' ||
    insumoTipoFilter === 'servico' ||
    insumoTipoFilter === 'limpeza_conservacao';

  // Base global ABINPET só faz sentido em Ingredientes. Embalagens/serviços/limpeza são só da empresa.
  const isCatalogoEmpresaOnly =
    insumoTipoFilter === 'embalagem' ||
    insumoTipoFilter === 'servico' ||
    insumoTipoFilter === 'limpeza_conservacao';
  const permiteBaseGlobal = !isRecebedor && !isCatalogoEmpresaOnly;
  const podeImportarDaBaseGlobal = permiteBaseGlobal && Boolean(companyId);

  const pageUi = (() => {
    if (insumoTipoFilter === 'embalagem') {
      return {
        titulo: 'Gestão de Embalagens',
        subtituloGerente: 'Gerencie as embalagens da sua empresa',
        subtituloRecebedor: 'Visualize as embalagens da sua empresa',
        subtituloGlobal: 'Explore embalagens cadastradas para a empresa',
        novoBtn: 'Nova Embalagem',
        statsTotalLabel: 'Total de Embalagens',
        confirmDelete: 'Tem certeza que deseja excluir esta embalagem?',
        sucessoCreate: 'Embalagem criada com sucesso!',
        sucessoUpdate: 'Embalagem atualizada com sucesso!',
        erroSave: 'Erro ao salvar embalagem',
        sucessoDelete: 'Embalagem excluída com sucesso!',
        erroDelete: 'Erro ao excluir embalagem',
      };
    }
    if (insumoTipoFilter === 'servico') {
      return {
        titulo: 'Serviços periódicos',
        subtituloGerente:
          'Cadastre serviços recorrentes na fábrica que impactam a qualidade (limpeza, dedetização, etc.)',
        subtituloRecebedor: 'Visualize os serviços periódicos cadastrados',
        subtituloGlobal: 'Serviços da empresa',
        novoBtn: 'Novo serviço',
        statsTotalLabel: 'Total de serviços',
        confirmDelete: 'Tem certeza que deseja excluir este serviço?',
        sucessoCreate: 'Serviço cadastrado com sucesso!',
        sucessoUpdate: 'Serviço atualizado com sucesso!',
        erroSave: 'Erro ao salvar serviço',
        sucessoDelete: 'Serviço excluído com sucesso!',
        erroDelete: 'Erro ao excluir serviço',
      };
    }
    if (insumoTipoFilter === 'limpeza_conservacao') {
      return {
        titulo: 'Limpeza e conservação',
        subtituloGerente: 'Gerencie produtos de limpeza, higienização e conservação do ambiente',
        subtituloRecebedor: 'Visualize os itens de limpeza e conservação da sua empresa',
        subtituloGlobal: 'Itens de limpeza e conservação da empresa',
        novoBtn: 'Novo item',
        statsTotalLabel: 'Total de itens',
        confirmDelete: 'Tem certeza que deseja excluir este item?',
        sucessoCreate: 'Item cadastrado com sucesso!',
        sucessoUpdate: 'Item atualizado com sucesso!',
        erroSave: 'Erro ao salvar item',
        sucessoDelete: 'Item excluído com sucesso!',
        erroDelete: 'Erro ao excluir item',
      };
    }
    return {
      titulo: 'Gestão de Ingredientes',
      subtituloGerente: 'Gerencie os ingredientes da sua empresa',
      subtituloRecebedor: 'Visualize os ingredientes da sua empresa',
      subtituloGlobal: 'Explore a base global de ingredientes ABINPET',
      novoBtn: 'Novo Ingrediente',
      statsTotalLabel: 'Total de Ingredientes',
      confirmDelete: 'Tem certeza que deseja excluir este ingrediente?',
      sucessoCreate: 'Ingrediente criado com sucesso!',
      sucessoUpdate: 'Ingrediente atualizado com sucesso!',
      erroSave: 'Erro ao salvar ingrediente',
      sucessoDelete: 'Ingrediente excluído com sucesso!',
      erroDelete: 'Erro ao excluir ingrediente',
    };
  })();

  const [viewMode, setViewMode] = useState(
    isSuperadmin ? 'global' : 'empresa' // Recebedor sempre 'empresa'
  );
  const [produtos, setProdutos] = useState([]);
  const [produtosGlobais, setProdutosGlobais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados de filtros
  const [filtros, setFiltros] = useState({
    nome: '',
    categoria: '',
    subcategoria: '',
    industria: '',
    status: 'todos'
  });

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(250); // ✅ AUMENTADO PARA 250

  // Estados do modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Estados de importação
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedForImport, setSelectedForImport] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  /** Seleção para alteração em lote de tipo_insumo (helper nas rotas /app/insumos/*) */
  const [selectedForTipoMigracao, setSelectedForTipoMigracao] = useState([]);
  const [tipoDestinoMigracao, setTipoDestinoMigracao] = useState('embalagem');
  const [migracaoTipoLoading, setMigracaoTipoLoading] = useState(false);

  const [fornecedoresModalProduto, setFornecedoresModalProduto] = useState(null);

  /** Catálogo parametrizado (Categorias_Insumos) para destacar insumos não aderentes */
  const [catalogoCategoriasRows, setCatalogoCategoriasRows] = useState([]);
  const [filtroAderenciaCategorias, setFiltroAderenciaCategorias] = useState('todos');

  // Estados de estatísticas
  const [stats, setStats] = useState({
    total: 0,
    porCategoria: {},
    porIndustria: {},
    porStatus: {}
  });

  const applyLocalSearchFilter = (items, searchValue) =>
    filterItemsBySearch(items, searchValue, [
      (item) => item.nome || item.nome_produto,
      (item) => item.codigo || item.codigo_produto || item.codigo_origem,
      (item) => item.subcategoria,
      (item) => item.categoria,
    ], { matchDigits: true });

  // Carregar produtos da empresa
  const loadProdutosEmpresa = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 loadProdutosEmpresa: companyId:', companyId);
      console.log('🔍 loadProdutosEmpresa: filtros:', filtros);
      
      if (!companyId) {
        console.warn('⚠️ loadProdutosEmpresa: companyId não definido');
        setProdutos([]);
        return;
      }

      // ✅ CORREÇÃO: Usar Cloud Function em vez de acesso direto ao Firestore
      const listProducts = httpsCallable(functions, 'listProducts');
      
      const params = {
        companyId: companyId,
        page: 1,
        limit: itemsPerPage,
        ativo: true, // ✅ Apenas produtos ativos
        industria: (filtros.industria && filtros.industria !== '' && filtros.industria !== 'todas') ? filtros.industria : undefined,
        categoria: (filtros.categoria && filtros.categoria !== '' && filtros.categoria !== 'todas') ? filtros.categoria : undefined,
        search: filtros.nome?.trim() ? sanitizeSearchQuery(filtros.nome) : undefined,
      };
      
      // ✅ LIMPAR parâmetros undefined para não enviá-los ao backend
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      console.log('🔍 loadProdutosEmpresa: Chamando listProducts com params:', JSON.stringify(params, null, 2));
      
      const result = await listProducts(params);

      console.log('🔍 loadProdutosEmpresa: Resultado recebido:', result);
      
      if (result.data?.success) {
        let produtosList = result.data.data.products;
        if (insumoTipoFilter) {
          produtosList = produtosList.filter(
            (p) => tipoInsumoNormalizado(p) === insumoTipoFilter
          );
        }
        console.log(`✅ loadProdutosEmpresa: ${produtosList.length} produtos (após filtro tipo)`);
        const filteredList = applyLocalSearchFilter(produtosList, filtros.nome);
        setProdutos(filteredList);
        calculateStats(produtosList);
      } else {
        console.error('❌ loadProdutosEmpresa: Erro no resultado:', result.data);
        throw new Error(result.data?.message || 'Erro ao carregar produtos da empresa');
      }

    } catch (error) {
      console.error('❌ loadProdutosEmpresa: Erro ao carregar produtos da empresa:', error);
      console.error('❌ loadProdutosEmpresa: Error message:', error.message);
      console.error('❌ loadProdutosEmpresa: Error code:', error.code);
      console.error('❌ loadProdutosEmpresa: Error details:', error.details);
      setError('Erro ao carregar produtos da empresa');
    } finally {
      setLoading(false);
    }
  };

  // Carregar produtos globais (base ABINPET)
  const loadProdutosGlobais = async () => {
    try {
      setLoading(true);

      // ✅ CORREÇÃO: Usar Cloud Function em vez de acesso direto ao Firestore
      const listProdutosRegulaMais = httpsCallable(functions, 'listProdutosRegulaMais');
      
      const result = await listProdutosRegulaMais({
        page: 1,
        limit: itemsPerPage,
        industria: filtros.industria !== 'todas' ? filtros.industria : undefined,
        categoria: filtros.categoria !== 'todas' ? filtros.categoria : undefined,
        subcategoria: filtros.subcategoria !== 'todas' ? filtros.subcategoria : undefined,
        search: filtros.nome?.trim() ? sanitizeSearchQuery(filtros.nome) : undefined,
      });

      if (result.data?.success) {
        const produtosList = result.data.data.produtos;
        const filteredList = applyLocalSearchFilter(produtosList, filtros.nome);
        setProdutosGlobais(filteredList);
        calculateStatsGlobal(produtosList);
      } else {
        throw new Error(result.data?.message || 'Erro ao carregar produtos globais');
      }

    } catch (error) {
      console.error('Erro ao carregar produtos globais:', error);
      setError('Erro ao carregar produtos globais');
    } finally {
      setLoading(false);
    }
  };

  // Carregar TODOS os produtos para estatísticas corretas
  const loadAllProductsForStats = async () => {
    try {
      if (viewMode === 'empresa' || isRecebedor || isCatalogoEmpresaOnly) {
        if (!companyId) return;
        
        // ✅ CORREÇÃO: Usar Cloud Function para estatísticas
        const listProducts = httpsCallable(functions, 'listProducts');
        
        const result = await listProducts({
          companyId: companyId,
          page: 1,
          limit: 1000 // Buscar todos os produtos para estatísticas
        });
        
        if (result.data?.success) {
          let allProducts = result.data.data.products;
          if (insumoTipoFilter) {
            allProducts = allProducts.filter(
              (p) => tipoInsumoNormalizado(p) === insumoTipoFilter
            );
          }
          calculateStats(allProducts);
        }
      } else {
        // ✅ CORREÇÃO: Usar Cloud Function para produtos globais
        const listProdutosRegulaMais = httpsCallable(functions, 'listProdutosRegulaMais');
        
        const result = await listProdutosRegulaMais({
          page: 1,
          limit: 1000 // Buscar todos os produtos para estatísticas
        });
        
        if (result.data?.success) {
          const allProducts = result.data.data.produtos;
          calculateStatsGlobal(allProducts);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  // Calcular estatísticas dos produtos da empresa
  const calculateStats = (produtosList) => {
    const newStats = {
      total: produtosList.length,
      porCategoria: {},
      porIndustria: {},
      porStatus: {}
    };

    produtosList.forEach(produto => {
      // Por categoria
      const categoria = produto.categoria || 'Não definida';
      newStats.porCategoria[categoria] = (newStats.porCategoria[categoria] || 0) + 1;

      // Por indústria
      const industria = produto.industria || 'Não definida';
      newStats.porIndustria[industria] = (newStats.porIndustria[industria] || 0) + 1;

      // Por status
      const status = produto.status || 'ativo';
      newStats.porStatus[status] = (newStats.porStatus[status] || 0) + 1;
    });

    setStats(newStats);
  };

  // Calcular estatísticas dos produtos globais
  const calculateStatsGlobal = (produtosList) => {
    const newStats = {
      total: produtosList.length,
      porCategoria: {},
      porIndustria: {},
      porStatus: {}
    };

    produtosList.forEach(produto => {
      // Por categoria
      const categoria = produto.categoria || 'Não definida';
      newStats.porCategoria[categoria] = (newStats.porCategoria[categoria] || 0) + 1;

      // Por indústria
      const industria = produto.industria || 'Não definida';
      newStats.porIndustria[industria] = (newStats.porIndustria[industria] || 0) + 1;

      // Status sempre ativo para produtos globais
      newStats.porStatus['ativo'] = produtosList.length;
    });

    setStats(newStats);
  };

  // Aplicar filtros
  const applyFilters = () => {
    setCurrentPage(1);
    if (viewMode === 'empresa') {
      loadProdutosEmpresa();
    } else {
      loadProdutosGlobais();
    }
  };

  // Limpar filtros
  const clearFilters = () => {
    setFiltros({
      nome: '',
      categoria: '',
      subcategoria: '',
      industria: '',
      status: 'todos'
    });
    setFiltroAderenciaCategorias('todos');
    setCurrentPage(1);
    if (isSuperadmin || viewMode === 'global') {
      loadProdutosGlobais();
    } else {
      loadProdutosEmpresa();
    }
  };

  // Alternar modo de visualização
  const toggleViewMode = (mode) => {
    if (!permiteBaseGlobal) {
      return;
    }
    
    setViewMode(mode);
    setCurrentPage(1);
    setSelectedForImport([]);
    
    // ✅ LIMPAR FILTROS ao mudar de visualização para evitar erro 500
    setFiltros({
      nome: '',
      categoria: '',
      subcategoria: '',
      industria: '',
      status: 'todos'
    });
  };

  // Abrir modal de produto
  const openProductModal = (mode, product = null) => {
    setModalMode(mode);
    setSelectedProduct(product);
    setShowModal(true);
  };

  // Fechar modal de produto
  const closeProductModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
    setModalMode('create');
  };

  // Salvar produto (callback do modal)
  const handleSaveProduct = async (productData) => {
    try {
      if (modalMode === 'create') {
        // ✅ NOVO: Verificar se é produto global ou da empresa
        if (viewMode === 'global' && isSuperadmin) {
          // Criar produto na base global
          const result = await createProdutoRegulaMais(productData);
          
          if (result.data?.success) {
            alert('Ingrediente global criado com sucesso!');
            loadProdutosGlobais();
          } else {
            throw new Error(result.data?.message || 'Erro ao criar produto global');
          }
        } else {
          // Criar produto da empresa via Cloud Function
          const empresaId = companyId || getEmpresaId();
          
          if (!empresaId) {
            throw new Error('ID da empresa não encontrado');
          }

          const result = await createProductFn({
            empresa_id: empresaId,
            ...productData
          });

          if (result.data?.success) {
            alert(pageUi.sucessoCreate);
            loadProdutosEmpresa();
          } else {
            throw new Error(result.data?.message || 'Erro ao criar item');
          }
        }
      } else if (modalMode === 'edit') {
        // ✅ NOVO: Verificar se é produto global ou da empresa
        if (viewMode === 'global' && isSuperadmin) {
          // Editar produto global
          const result = await updateProdutoRegulaMais({
            produtoId: selectedProduct.id,
            ...productData
          });
          
          if (result.data?.success) {
            alert('Ingrediente global atualizado com sucesso!');
            loadProdutosGlobais();
          } else {
            throw new Error(result.data?.message || 'Erro ao atualizar produto global');
          }
        } else {
          // Editar produto da empresa via Cloud Function
          const empresaId = companyId || getEmpresaId();

          if (!empresaId) {
            throw new Error('ID da empresa não encontrado');
          }

          const result = await updateProductFn({
            productId: selectedProduct.id,
            empresa_id: empresaId,
            ...productData
          });

          if (result.data?.success) {
            alert(pageUi.sucessoUpdate);
            loadProdutosEmpresa();
          } else {
            throw new Error(result.data?.message || 'Erro ao atualizar item');
          }
        }
      }

      closeProductModal();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert(pageUi.erroSave);
    }
  };

  // Deletar produto da empresa
  const handleDeleteProduct = async (productId) => {
    const empresaId = companyId || getEmpresaId();
    const produto = produtos.find((p) => p.id === productId);

    if (!empresaId) {
      alert('Empresa não identificada. Não é possível excluir o insumo.');
      return;
    }

    try {
      const consultarSaldoEstoque = httpsCallable(functions, 'consultarSaldoEstoqueParaExclusao');
      const checagem = await verificarEstoqueAntesExcluirInsumo({
        consultarSaldoFn: consultarSaldoEstoque,
        productId,
        empresaId,
        produto: produto || { id: productId, nome: '' },
        pularSeServico: insumoTipoFilter === 'servico' || produto?.tipo_insumo === 'servico',
      });

      if (!checagem.podeExcluir) {
        alert(
          mensagemBloqueioExclusaoPorEstoque({
            nome: checagem.nome,
            quantidade_atual: checagem.quantidade_atual,
            unidade: checagem.unidade,
            quantidade_recebimentos: checagem.quantidade_recebimentos,
            lotes_recebimentos: checagem.lotes_recebimentos,
          })
        );
        return;
      }
    } catch (errEstoque) {
      console.error('Verificação de estoque antes da exclusão falhou:', errEstoque);
      alert(
        `Não foi possível verificar o saldo em estoque. A exclusão foi cancelada por segurança.\n\n${
          errEstoque?.message || 'Tente novamente ou confira se as functions foram publicadas.'
        }`
      );
      return;
    }

    if (!window.confirm(pageUi.confirmDelete)) {
      return;
    }

    try {
      const deleteProduct = httpsCallable(functions, 'deleteProduct');

      const result = await deleteProduct({
        productId: productId,
        empresa_id: empresaId,
      });

      if (result.data?.success) {
        loadProdutosEmpresa();
        alert(pageUi.sucessoDelete);
      } else {
        throw new Error(result.data?.message || 'Erro ao excluir');
      }
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert(parseErroExclusaoInsumo(error));
    }
  };

  // ✅ NOVO: Deletar produto global (apenas superadmin)
  const handleDeleteGlobalProduct = async (productId) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto da base global? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const result = await deleteProdutoRegulaMais({ produtoId: productId });
      
      if (result.data?.success) {
        // Recarregar lista
        loadProdutosGlobais();
        alert('Ingrediente global excluído com sucesso!');
      } else {
        throw new Error(result.data?.message || 'Erro ao excluir produto global');
      }
    } catch (error) {
      console.error('Erro ao excluir produto global:', error);
      alert('Erro ao excluir produto global');
    }
  };

  // Selecionar produto para importação
  const toggleProductSelection = (productId) => {
    setSelectedForImport(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Selecionar todos os produtos para importação
  const toggleSelectAll = () => {
    if (selectedForImport.length === produtosGlobais.length) {
      setSelectedForImport([]);
    } else {
      setSelectedForImport(produtosGlobais.map(p => p.id));
    }
  };

  const toggleTipoMigracaoSelection = (productId) => {
    setSelectedForTipoMigracao((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const toggleSelectAllTipoMigracao = () => {
    if (selectedForTipoMigracao.length === produtos.length && produtos.length > 0) {
      setSelectedForTipoMigracao([]);
    } else {
      setSelectedForTipoMigracao(produtos.map((p) => p.id));
    }
  };

  const handleBulkSetTipoInsumo = async () => {
    if (selectedForTipoMigracao.length === 0) {
      alert('Selecione pelo menos um insumo na lista.');
      return;
    }
    if (!tipoDestinoMigracao || tipoDestinoMigracao === insumoTipoFilter) {
      alert('Escolha um tipo de destino diferente do menu (aba) em que você está.');
      return;
    }
    const destinoLabel =
      OPCOES_TIPO_INSUMO_DESTINO.find((o) => o.value === tipoDestinoMigracao)?.label || tipoDestinoMigracao;
    if (
      !window.confirm(
        `Alterar o tipo de ${selectedForTipoMigracao.length} item(ns) para "${destinoLabel}"? Eles passarão a aparecer no menu correspondente.`
      )
    ) {
      return;
    }
    const empresaId = companyId || getEmpresaId();
    if (!empresaId) {
      alert('ID da empresa não encontrado.');
      return;
    }
    try {
      setMigracaoTipoLoading(true);
      setError(null);
      const bulkSetTipoInsumoProducts = httpsCallable(functions, 'bulkSetTipoInsumoProducts');
      const result = await bulkSetTipoInsumoProducts({
        empresa_id: empresaId,
        product_ids: selectedForTipoMigracao,
        tipo_insumo: tipoDestinoMigracao,
      });
      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Falha ao atualizar tipos');
      }
      const n = result.data?.data?.atualizados ?? 0;
      alert(`Tipo atualizado em ${n} produto(s).`);
      setSelectedForTipoMigracao([]);
      loadProdutosEmpresa();
      loadAllProductsForStats();
    } catch (e) {
      console.error(e);
      const msg = e.message || 'Erro ao alterar tipo em lote';
      setError(msg);
      alert(msg);
    } finally {
      setMigracaoTipoLoading(false);
    }
  };

  // Importar produtos selecionados
  const handleImportProducts = async () => {
    if (selectedForImport.length === 0) {
      alert('Selecione pelo menos um produto para importar');
      return;
    }

    if (!window.confirm(`Importar ${selectedForImport.length} produto(s) para sua empresa?`)) {
      return;
    }

    // ✅ DEBUG: Log do companyId
    console.log('🔍 ProductsPage: companyId para importação:', companyId);
    console.log('🔍 ProductsPage: user:', user);
    console.log('🔍 ProductsPage: userProfile:', userProfile);
    console.log('🔍 ProductsPage: getEmpresaId():', getEmpresaId());

    try {
      setImportLoading(true);
      setError(null);

      // ✅ CORREÇÃO: Usar getEmpresaId() como fallback se companyId for undefined
      const empresaId = companyId || getEmpresaId();
      
      if (!empresaId) {
        throw new Error('ID da empresa não encontrado');
      }

      // ✅ CORREÇÃO: Usar Cloud Function em vez de addDoc direto
      console.log('🔍 ProductsPage: Chamando importarProdutosDaBase com:', {
        empresa_id: empresaId,
        produto_ids: selectedForImport
      });

      const result = await importarProdutosDaBase({
        empresa_id: empresaId,
        produto_ids: selectedForImport
      });

      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Erro ao importar produtos');
      }

      const importedProducts = result.data.data?.imported_products || [];
      const errors = result.data.data?.errors || [];
      const sucessos = importedProducts.length;
      const erros = errors.length;

      console.log(`📊 Importação concluída: ${sucessos} sucessos, ${erros} erros`);

      // Limpar seleção e fechar modal
      setSelectedForImport([]);
      setShowImportModal(false);
      
      // ✅ MANTÉM NA BASE GLOBAL: Não recarrega produtos da empresa para evitar erro
      // O usuário pode manualmente mudar para "Minha Empresa" quando quiser
      console.log('✅ Importação concluída. Permanecendo na Base Global.');
      
      // Mostrar mensagem de sucesso
      if (erros === 0) {
        alert(`✅ ${sucessos} produto(s) importado(s) com sucesso!\n\n💡 Acesse "Minha Empresa" para visualizar os produtos importados.`);
      } else {
        const errorMessages = errors.join('\n');
        alert(`⚠️ Importação concluída:\n${sucessos} sucesso(s)\n${erros} erro(s)\n\nErros:\n${errorMessages}`);
      }

    } catch (error) {
      console.error('❌ Erro ao importar produtos:', error);
      setError('Erro ao importar produtos: ' + error.message);
      alert('Erro ao importar produtos. Verifique o console para mais detalhes.');
    } finally {
      setImportLoading(false);
    }
  };

  // Exportar produtos
  const handleExportProducts = () => {
    const dataToExport = viewMode === 'empresa' ? produtos : produtosGlobais;
    
    const csvContent = [
      // Cabeçalho
      ['Nome', 'Categoria', 'Subcategoria', 'Indústria', 'Status', 'Parâmetros'].join(','),
      // Dados
      ...dataToExport.map(produto => [
        produto.nome || produto.nome_produto || '',
        produto.categoria || '',
        produto.subcategoria || '',
        produto.industria || '',
        produto.status || 'ativo',
        (produto.parametros_qualidade || produto.parametros || []).length // ✅ NORMALIZADO
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `produtos_${viewMode}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (isCatalogoEmpresaOnly) {
      setViewMode('empresa');
    }
  }, [isCatalogoEmpresaOnly]);

  // Efeitos
  useEffect(() => {
    const verBaseGlobal = viewMode === 'global' && permiteBaseGlobal;

    if (!verBaseGlobal) {
      if (!companyId) {
        setLoading(false);
        return;
      }
      loadProdutosEmpresa();
      loadAllProductsForStats();
      return;
    }

    loadProdutosGlobais();
    loadAllProductsForStats();
  }, [
    viewMode,
    currentPage,
    companyId,
    isSuperadmin,
    isRecebedor,
    isInsumoRota,
    insumoTipoFilter,
    permiteBaseGlobal,
  ]);

  useEffect(() => {
    clearFilters();
  }, [viewMode, insumoTipoFilter]);

  useEffect(() => {
    if (!isInsumoRota) return;
    setSelectedForTipoMigracao([]);
    const next =
      OPCOES_TIPO_INSUMO_DESTINO.find((o) => o.value !== insumoTipoFilter)?.value || 'ingrediente';
    setTipoDestinoMigracao(next);
  }, [insumoTipoFilter, isInsumoRota]);

  useEffect(() => {
    if (!isInsumoRota || !insumoTipoFilter) {
      setCatalogoCategoriasRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const q = query(
          collection(db, 'Categorias_Insumos'),
          where('tipo_insumo', '==', insumoTipoFilter)
        );
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!cancelled) setCatalogoCategoriasRows(rows);
      } catch (err) {
        console.error('Erro ao carregar Categorias_Insumos:', err);
        if (!cancelled) setCatalogoCategoriasRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInsumoRota, insumoTipoFilter]);

  const catalogoCategoriasMap = useMemo(
    () => buildCatalogoMapFromRows(catalogoCategoriasRows),
    [catalogoCategoriasRows]
  );

  const showAderenciaCategorias = isInsumoRota && viewMode === 'empresa';

  const produtosEmpresaComAderencia = useMemo(() => {
    if (!showAderenciaCategorias) return produtos;
    return produtos.map((p) => ({
      ...p,
      _aderenciaCategorias: avaliarAderenciaCategorias(p, catalogoCategoriasMap),
    }));
  }, [produtos, showAderenciaCategorias, catalogoCategoriasMap]);

  const produtosEmpresaExibidos = useMemo(() => {
    if (!showAderenciaCategorias) return produtos;
    if (filtroAderenciaCategorias === 'nao_aderentes') {
      return produtosEmpresaComAderencia.filter((p) => !p._aderenciaCategorias?.aderente);
    }
    if (filtroAderenciaCategorias === 'aderentes') {
      return produtosEmpresaComAderencia.filter((p) => p._aderenciaCategorias?.aderente);
    }
    return produtosEmpresaComAderencia;
  }, [produtosEmpresaComAderencia, showAderenciaCategorias, filtroAderenciaCategorias]);

  const totalNaoAderentesCategorias = useMemo(() => {
    if (!showAderenciaCategorias) return 0;
    return produtosEmpresaComAderencia.filter((p) => !p._aderenciaCategorias?.aderente).length;
  }, [produtosEmpresaComAderencia, showAderenciaCategorias]);

  // Obter listas únicas para filtros
  const getUniqueValues = (field) => {
    const dataSource = viewMode === 'empresa' ? produtos : produtosGlobais;
    const values = dataSource.map(item => {
      if (viewMode === 'empresa') {
        return item[field];
      } else {
        return field === 'nome' ? item.nome_produto : item[field];
      }
    }).filter(Boolean);
    return [...new Set(values)].sort();
  };

  const showHelperMigracao =
    EXIBIR_HELPER_REORGANIZAR_TIPO_INSUMO &&
    isInsumoRota &&
    viewMode === 'empresa' &&
    hasPermission('manage_products');

  // Renderizar card de produto
  const renderProductCard = (produto, isGlobal = false) => {
    const nome = isGlobal ? produto.nome_produto : produto.nome;
    const parametros = produto.parametros_qualidade || produto.parametros || []; // ✅ NORMALIZADO
    const parametrosCount = parametros.length;
    const isServicoCard = insumoTipoFilter === 'servico';
    const aderencia = !isGlobal ? produto._aderenciaCategorias : null;
    const naoAderenteCategorias =
      !isGlobal && aderencia && !aderencia.aderente && !aderencia.catalogoVazio;

    return (
      <div
        key={produto.id}
        className={`rounded-lg p-6 transition-shadow ${
          naoAderenteCategorias
            ? 'border-2 border-amber-300 bg-amber-50 shadow-sm hover:border-amber-400 hover:shadow-md'
            : 'bg-white shadow-md hover:shadow-lg'
        }`}
      >
        {/* Header do card */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900">{nome}</h3>
              {naoAderenteCategorias && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-200/90 px-2 py-0.5 text-xs font-medium text-amber-950"
                  title={rotuloMotivosAderencia(aderencia.motivos)}
                >
                  <AlertCircle className="h-3 w-3 shrink-0" aria-hidden />
                  Fora do padrão
                </span>
              )}
            </div>
            {naoAderenteCategorias && (
              <p className="mb-1 text-xs text-amber-900">
                {rotuloMotivosAderencia(aderencia.motivos)} — edite o cadastro para alinhar à parametrização.
              </p>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Tag className="w-4 h-4" />
              <span>{produto.categoria}</span>
              {produto.subcategoria && (
                <>
                  <span>•</span>
                  <span>{produto.subcategoria}</span>
                </>
              )}
            </div>
          </div>
          
          {showHelperMigracao && !isGlobal && (
            <div className="ml-4 shrink-0">
              <input
                type="checkbox"
                checked={selectedForTipoMigracao.includes(produto.id)}
                onChange={() => toggleTipoMigracaoSelection(produto.id)}
                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                aria-label={`Selecionar para alterar tipo: ${nome}`}
              />
            </div>
          )}

          {/* Checkbox para seleção (modo global, com empresa para importar) */}
          {viewMode === 'global' && podeImportarDaBaseGlobal && (
            <div className="ml-4">
              <input
                type="checkbox"
                checked={selectedForImport.includes(produto.id)}
                onChange={() => toggleProductSelection(produto.id)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>
          )}
        </div>

        {/* Informações do produto */}
        <div className="space-y-2 mb-4">
          {!isServicoCard && (
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Indústria:</span>
            <span className="font-medium">{produto.industria}</span>
          </div>
          )}

          {isServicoCard ? (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Frequência:</span>
              <span className="font-medium">
                {labelFrequenciaExecucao(produto.frequencia_execucao)}
              </span>
            </div>
          ) : (
          <div className="flex items-center gap-2 text-sm">
            <Package className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Parâmetros:</span>
            <span className="font-medium">{parametrosCount} critério(s)</span>
          </div>
          )}

          {!isGlobal && produto.status && (
            <div className="flex items-center gap-2 text-sm">
              {produto.status === 'ativo' ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : produto.status === 'inativo' ? (
                <X className="w-4 h-4 text-red-500" />
              ) : (
                <Clock className="w-4 h-4 text-yellow-500" />
              )}
              <span className="text-gray-600">Status:</span>
              <span className={`font-medium ${
                produto.status === 'ativo' ? 'text-green-600' :
                produto.status === 'inativo' ? 'text-red-600' : 'text-yellow-600'
              }`}>
                {produto.status}
              </span>
            </div>
          )}

          {isGlobal && produto.codigo_produto && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Código:</span>
              <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                {produto.codigo_produto}
              </span>
            </div>
          )}
        </div>

        {/* Ações */}
        <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-gray-100">
          {!isGlobal && (
            <button
              type="button"
              onClick={() => setFornecedoresModalProduto(produto)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
            >
              <Truck className="w-4 h-4" />
              Fornecedores
            </button>
          )}
          <button
            type="button"
            onClick={() => openProductModal('view', produto)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Eye className="w-4 h-4" />
            Ver
          </button>
          
          {/* ✅ CORREÇÃO: Verificar permissão manage_products antes de mostrar botões de editar/excluir */}
          {(!isGlobal || isSuperadmin) && hasPermission('manage_products') && (
            <>
              <button
                onClick={() => openProductModal('edit', produto)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-md transition-colors"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
              
              <button
                onClick={() => isGlobal ? handleDeleteGlobalProduct(produto.id) : handleDeleteProduct(produto.id)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // Renderizar estatísticas
  const renderStats = () => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{pageUi.statsTotalLabel}</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <Package className="w-8 h-8 text-blue-500" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Categorias</p>
            <p className="text-2xl font-bold text-gray-900">{Object.keys(stats.porCategoria).length}</p>
          </div>
          <Tag className="w-8 h-8 text-green-500" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Indústrias</p>
            <p className="text-2xl font-bold text-gray-900">{Object.keys(stats.porIndustria).length}</p>
          </div>
          <Building2 className="w-8 h-8 text-purple-500" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">
              {viewMode === 'empresa' ? 'Ativos' : 'Disponíveis'}
            </p>
            <p className="text-2xl font-bold text-gray-900">
              {viewMode === 'empresa' ? (stats.porStatus.ativo || 0) : stats.total}
            </p>
          </div>
          <CheckCircle className="w-8 h-8 text-emerald-500" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{pageUi.titulo}</h1>
              <p className="text-gray-600 mt-1">
                {isRecebedor
                  ? pageUi.subtituloRecebedor
                  : viewMode === 'global'
                    ? pageUi.subtituloGlobal
                    : pageUi.subtituloGerente}
              </p>
            </div>

            {permiteBaseGlobal && (
              <div className="flex bg-white rounded-lg shadow-sm border">
                <button
                  onClick={() => toggleViewMode('empresa')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                    viewMode === 'empresa'
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {isFornecedor ? 'Meus Ingredientes' : 'Minha Empresa'}
                </button>
                <button
                  onClick={() => toggleViewMode('global')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                    viewMode === 'global'
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Base Global
                </button>
              </div>
            )}
          </div>

          {/* Estatísticas */}
          {renderStats()}
        </div>

        {showHelperMigracao && (
          <div className="mb-6 space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 shadow-sm">
            <div className="flex gap-3">
              <Info className="h-5 w-5 shrink-0 text-amber-700" aria-hidden />
              <div>
                <p className="font-medium text-amber-900">Reorganizar insumos entre os quatro menus</p>
                <p className="mt-1 text-amber-800">
                  Marque os itens da lista abaixo, escolha o <strong>novo tipo</strong> e confirme. Cada item passa a
                  aparecer no menu correspondente (Ingredientes, Embalagens, Serviços ou Limpeza e conservação). Isto
                  não altera vínculos com fornecedores.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3 border-t border-amber-200/80 pt-3 sm:flex-row sm:flex-wrap sm:items-end">
              <div>
                <label htmlFor="tipo-destino-migracao" className="mb-1 block text-xs font-medium text-amber-900">
                  Novo tipo (destino)
                </label>
                <select
                  id="tipo-destino-migracao"
                  value={tipoDestinoMigracao}
                  onChange={(e) => setTipoDestinoMigracao(e.target.value)}
                  className="w-full min-w-[220px] rounded-md border border-amber-300 bg-white px-3 py-2 text-gray-900 sm:w-auto"
                >
                  {OPCOES_TIPO_INSUMO_DESTINO.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.value === insumoTipoFilter}>
                      {o.label}
                      {o.value === insumoTipoFilter ? ' (menu atual)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={toggleSelectAllTipoMigracao}
                disabled={migracaoTipoLoading || produtos.length === 0}
                className="rounded-md border border-amber-400 bg-white px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              >
                {selectedForTipoMigracao.length === produtos.length && produtos.length > 0
                  ? 'Desmarcar todos'
                  : 'Selecionar todos da lista'}
              </button>
              <button
                type="button"
                onClick={handleBulkSetTipoInsumo}
                disabled={
                  migracaoTipoLoading ||
                  selectedForTipoMigracao.length === 0 ||
                  tipoDestinoMigracao === insumoTipoFilter
                }
                className="inline-flex items-center gap-2 rounded-md bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800 disabled:opacity-50"
              >
                {migracaoTipoLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Aplicando…
                  </>
                ) : (
                  `Aplicar tipo (${selectedForTipoMigracao.length})`
                )}
              </button>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* ✅ 1. NOME */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input
                type="text"
                value={filtros.nome}
                onChange={(e) => setFiltros(prev => ({ ...prev, nome: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="Buscar por nome..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* ✅ 2. INDÚSTRIA */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Indústria</label>
              <select
                value={filtros.industria}
                onChange={(e) => setFiltros(prev => ({ ...prev, industria: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas as indústrias</option>
                {getUniqueValues('industria').map(industria => (
                  <option key={industria} value={industria}>{industria}</option>
                ))}
              </select>
            </div>

            {/* ✅ 3. CATEGORIA */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
              <select
                value={filtros.categoria}
                onChange={(e) => setFiltros(prev => ({ ...prev, categoria: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas as categorias</option>
                {getUniqueValues('categoria').map(categoria => (
                  <option key={categoria} value={categoria}>{categoria}</option>
                ))}
              </select>
            </div>

            {/* ✅ 4. SUBCATEGORIA */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subcategoria</label>
              <select
                value={filtros.subcategoria}
                onChange={(e) => setFiltros(prev => ({ ...prev, subcategoria: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas as subcategorias</option>
                {getUniqueValues('subcategoria').map(subcategoria => (
                  <option key={subcategoria} value={subcategoria}>{subcategoria}</option>
                ))}
              </select>
            </div>

            {showAderenciaCategorias && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Padronização de categorias
                </label>
                <select
                  value={filtroAderenciaCategorias}
                  onChange={(e) => setFiltroAderenciaCategorias(e.target.value)}
                  className="w-full px-3 py-2 border border-amber-200 rounded-md bg-amber-50/50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="todos">Todos os insumos</option>
                  <option value="nao_aderentes">
                    Somente fora do padrão
                    {totalNaoAderentesCategorias > 0 ? ` (${totalNaoAderentesCategorias})` : ''}
                  </option>
                  <option value="aderentes">Somente aderentes ao padrão</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={applyFilters}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              <Search className="w-4 h-4" />
              Aplicar Filtros
            </button>
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
              Limpar
            </button>
          </div>
        </div>

        {/* Ações */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2">
            {/* ✅ NOVO: Botão de criar para empresa OU para superadmin na base global */}
            {/* ✅ CORREÇÃO: Verificar permissão manage_products antes de mostrar botão */}
            {(viewMode === 'empresa' || (viewMode === 'global' && isSuperadmin)) && hasPermission('manage_products') && (
              <button
                onClick={() => openProductModal('create')}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {pageUi.novoBtn}
              </button>
            )}

            {viewMode === 'global' && podeImportarDaBaseGlobal && (
              <button
                onClick={() => setShowImportModal(true)}
                disabled={selectedForImport.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4" />
                Importar Selecionados ({selectedForImport.length})
              </button>
            )}
          </div>

          <div className="flex gap-2">
            {viewMode === 'global' && podeImportarDaBaseGlobal && (
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                <ClipboardList className="w-4 h-4" />
                {selectedForImport.length === produtosGlobais.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </button>
            )}

            <button
              onClick={handleExportProducts}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
            >
              <Download className="w-4 h-4" />
              Exportar
            </button>
          </div>
        </div>

        {/* Lista de produtos */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {showAderenciaCategorias && totalNaoAderentesCategorias > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-700" aria-hidden />
            <span>
              <strong>{totalNaoAderentesCategorias}</strong> insumo(s) com categoria ou subcategoria fora do
              padrão parametrizado (cards em destaque amarelo).
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {viewMode === 'empresa'
            ? produtosEmpresaExibidos.map((produto) => renderProductCard(produto, false))
            : produtosGlobais.map((produto) => renderProductCard(produto, true))}
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-8">
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              
              <span className="px-3 py-2 text-gray-700">
                Página {currentPage} de {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}

        {/* Modal de importação */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Confirmar Importação
                </h3>
                
                <p className="text-gray-600 mb-6">
                  Você está prestes a importar <strong>{selectedForImport.length} produto(s)</strong> da base global ABINPET para sua empresa.
                </p>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowImportModal(false)}
                    disabled={importLoading}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleImportProducts}
                    disabled={importLoading}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {importLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                    {importLoading ? 'Importando...' : 'Confirmar Importação'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de produto */}
        {showModal && (
          <ProductModal
            isOpen={showModal}
            onClose={closeProductModal}
            mode={modalMode}
            product={selectedProduct}
            onSave={handleSaveProduct}
            isGlobal={viewMode === 'global'}
            tipoInsumo={
              viewMode === 'global'
                ? 'ingrediente'
                : insumoTipoFilter || 'ingrediente'
            }
          />
        )}

        <ProdutoFornecedoresModal
          isOpen={Boolean(fornecedoresModalProduto)}
          onClose={() => setFornecedoresModalProduto(null)}
          produto={fornecedoresModalProduto}
          empresaId={companyId || getEmpresaId()}
        />
      </div>
    </div>
  );
};

export default ProductsPage;

